<template>
    <div class="notification-settings">
        <!-- 初始化中 -->
        <div v-if="isInitializing" class="status-box initializing">
            <i class="fa-solid fa-circle-notch fa-spin mr-2"></i>
            <span>正在初始化通知服務...</span>
        </div>

        <!-- 未支援通知 -->
        <div v-else-if="isSupported === false" class="status-box not-supported">
            <i class="fa-solid fa-triangle-exclamation mr-2"></i>
            <span>您的裝置不支援推送通知功能</span>
        </div>

        <!-- 已啟用通知 -->
        <div v-else-if="hasPermission && fcmToken" class="status-box enabled">
            <div class="status-content">
                <div class="status-info">
                    <i class="fa-solid fa-bell mr-2"></i>
                    <span class="status-text">通知已啟用</span>
                    <span v-if="isCordovaWebView" class="badge-app">APP</span>
                </div>
                <small class="token-preview">Token: {{ tokenPreview }}</small>
            </div>
            <button class="btn-disable" @click="handleDisableNotification">
                關閉通知
            </button>
        </div>

        <!-- iOS/Android 模擬器不支援推送通知 -->
        <div v-else-if="isCordovaWebView && isSimulator && !fcmToken" class="status-box simulator">
            <div class="status-content">
                <div class="status-info">
                    <i class="fa-solid fa-desktop mr-2"></i>
                    <span class="status-text">模擬器環境</span>
                </div>
                <p class="description">
                    iOS/Android 模擬器不支援推送通知功能，請使用真實設備測試
                </p>
            </div>
        </div>

        <!-- Cordova WebView 模式下沒有 Token（需要重新開啟 App） -->
        <div v-else-if="isCordovaWebView && !fcmToken" class="status-box disabled">
            <div class="status-content">
                <div class="status-info">
                    <i class="fa-solid fa-bell-slash mr-2"></i>
                    <span class="status-text">通知未啟用</span>
                </div>
                <p class="description">
                    請完全關閉 App 後重新開啟，系統會自動請求通知權限
                </p>
            </div>
        </div>

        <!-- 未啟用通知（Web 模式） -->
        <div v-else class="status-box disabled">
            <div class="status-content">
                <div class="status-info">
                    <i class="fa-solid fa-bell-slash mr-2"></i>
                    <span class="status-text">通知未啟用</span>
                </div>
                <p class="description">
                    開啟通知功能，接收農業資訊、氣象警報和重要訊息
                </p>
            </div>
            <button
                class="btn-enable"
                @click="handleRequestPermission"
                :disabled="requesting">
                <i v-if="requesting" class="fa-solid fa-circle-notch fa-spin mr-1"></i>
                {{ requesting ? '請求中...' : '啟用通知' }}
            </button>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
    name: 'NotificationSettings',
    data() {
        return {
            requesting: false,
            initializing: false
        }
    },
    computed: {
        ...mapState('notification', ['permission', 'fcmToken', 'isSupported', 'isCordovaWebView', 'isSimulator']),
        ...mapGetters('notification', ['hasPermission']),

        tokenPreview() {
            if (!this.fcmToken) return ''
            return this.fcmToken.substring(0, 20) + '...'
        },

        // 是否正在初始化（內部狀態或 Vuex 狀態）
        isInitializing() {
            return this.initializing || this.isSupported === null
        }
    },
    async mounted() {
        // 確保通知服務已初始化
        if (this.isSupported === null) {
            console.log('[NotificationSettings] 檢測到服務尚未初始化，開始初始化...')
            this.initializing = true
            try {
                await this.$store.dispatch('notification/initialize')
                console.log('[NotificationSettings] 初始化完成，isSupported:', this.$store.state.notification.isSupported)
            } catch (error) {
                console.error('[NotificationSettings] 初始化失敗:', error)
                // 即使失敗也要停止 loading 狀態
            } finally {
                this.initializing = false
            }
        } else {
            console.log('[NotificationSettings] 服務已初始化，isSupported:', this.isSupported)
        }
    },
    methods: {
        async handleRequestPermission() {
            this.requesting = true
            try {
                const granted = await this.$store.dispatch('notification/requestPermission')

                if (granted) {
                    this.$bvToast.toast('通知功能已成功啟用', {
                        title: '成功',
                        variant: 'success',
                        solid: true
                    })

                    // 成功啟用後，會在 store 中自動呼叫 trackDevice
                } else {
                    this.$bvToast.toast('通知權限被拒絕，請在瀏覽器或裝置設定中允許通知', {
                        title: '權限被拒絕',
                        variant: 'warning',
                        solid: true,
                        autoHideDelay: 5000
                    })
                }
            } catch (error) {
                console.error('[NotificationSettings] 啟用通知失敗:', error)
                this.$bvToast.toast(`啟用通知失敗: ${error.message}`, {
                    title: '錯誤',
                    variant: 'danger',
                    solid: true
                })
            } finally {
                this.requesting = false
            }
        },

        async handleDisableNotification() {
            const confirmed = confirm('確定要關閉通知功能嗎？')
            if (!confirmed) return

            try {
                await this.$store.dispatch('notification/deleteToken')
                this.$bvToast.toast('通知功能已關閉', {
                    title: '成功',
                    variant: 'info',
                    solid: true
                })
            } catch (error) {
                console.error('[NotificationSettings] 關閉通知失敗:', error)
                this.$bvToast.toast(`關閉通知失敗: ${error.message}`, {
                    title: '錯誤',
                    variant: 'danger',
                    solid: true
                })
            }
        }
    }
}
</script>

<style scoped>
.notification-settings {
    width: 100%;
}

/* 狀態框 */
.status-box {
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1rem;
}

.status-box.initializing {
    background-color: #f8f9fa;
    color: #6c757d;
    text-align: center;
}

.status-box.not-supported {
    background-color: #fff3cd;
    color: #856404;
    border: 1px solid #ffc107;
    text-align: center;
}

.status-box.enabled {
    background-color: #d4edda;
    border: 1px solid #28a745;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.status-box.disabled {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
}

.status-box.simulator {
    background-color: #e7f3ff;
    border: 1px solid #b8daff;
    color: #004085;
}

.status-content {
    flex: 1;
}

.status-info {
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
}

.status-text {
    font-weight: 600;
    font-size: 1rem;
}

.badge-app {
    display: inline-block;
    background-color: #007bff;
    color: white;
    font-size: 0.65rem;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    margin-left: 8px;
    vertical-align: middle;
}

.token-preview {
    color: #6c757d;
    font-size: 0.75rem;
    display: block;
    margin-left: 1.75rem;
}

.description {
    margin: 0.5rem 0 1rem 0;
    color: #6c757d;
    font-size: 0.875rem;
}

/* 按鈕 */
.btn-enable,
.btn-disable {
    padding: 0.5rem 1.5rem;
    border: none;
    border-radius: 4px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-enable {
    background-color: #28a745;
    color: white;
    width: 100%;
}

.btn-enable:hover:not(:disabled) {
    background-color: #218838;
}

.btn-enable:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
}

.btn-disable {
    background-color: #dc3545;
    color: white;
}

.btn-disable:hover {
    background-color: #c82333;
}

/* 圖示動畫 */
.fa-spin {
    animation: fa-spin 1s infinite linear;
}

@keyframes fa-spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
</style>
